import React, { useState } from 'react';

const HAND_ITEMS = [
  { id: 'reaching',    label: 'Reaching',          findings: ['Normal','Reduced range','Inaccurate','Tremor during reach','Compensatory pattern','Unable'] },
  { id: 'grasping',   label: 'Grasping',           findings: ['Normal grip','Weak grip','Adapted grip','No finger flexion','Mass grasp only','Unable'] },
  { id: 'releasing',  label: 'Releasing',          findings: ['Normal','Delayed','Trigger finger','Incomplete release','Unable'] },
  { id: 'pinch',      label: 'Pinch Grip',         findings: ['Normal','Weak','Lateral pinch only','No opposition','Unable'] },
  { id: 'in_hand',    label: 'In-Hand Manipulation',findings: ['Normal','Impaired','Unable','Not assessed'] },
  { id: 'bimanual',   label: 'Bimanual Activities', findings: ['Normal','Impaired','One-hand compensation','Unable'] },
];

const HAND_GRADES = [
  { id: 'N',  label: 'Normal',   color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
  { id: 'I',  label: 'Impaired', color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
  { id: 'U',  label: 'Unable',   color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
  { id: 'NA', label: 'N/A',      color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1' },
];

const ASSISTIVE_HAND_OPTIONS = ['None required','Adapted grip handle','Universal cuff','Plate guard','Splint (static)','Splint (dynamic)','Wrist support','Environmental modification','Assistive technology'];

const HandFunctionAssessment = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(false);

  const toggle = (key, f) => {
    const curr = value[key] || [];
    onChange?.({ ...value, [key]: curr.includes(f) ? curr.filter(x => x !== f) : [...curr, f] });
  };
  const set = (key, val) => onChange?.({ ...value, [key]: value[key] === val ? null : val });

  const assessedCount = HAND_ITEMS.filter(it => (value[`hand_rt_${it.id}`]?.length || 0) + (value[`hand_lt_${it.id}`]?.length || 0) > 0).length;
  const imparedCount = HAND_ITEMS.filter(it => {
    const all = [...(value[`hand_rt_${it.id}`] || []), ...(value[`hand_lt_${it.id}`] || [])];
    return all.some(f => f !== 'Normal' && f !== 'Normal grip' && f !== 'Not assessed');
  }).length;

  const GradeBtn = ({ id, gid }) => {
    const active = value[id] === gid;
    const g = HAND_GRADES.find(x => x.id === gid);
    return (
      <button type="button" onClick={() => set(id, gid)} style={{
        fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.15s',
        border: `1px solid ${active ? g.border : '#e2e8f0'}`,
        background: active ? g.bg : 'transparent',
        color: active ? g.color : '#94a3b8'
      }}>{active ? '✓ ' : ''}{g.label}</button>
    );
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{
          width: '100%', padding: '15px', borderRadius: '14px',
          border: '1px solid #e2e8f0', backgroundColor: 'transparent', color: '#9d174d',
          fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', transition: 'all 0.2s'
        }}>
          + Hand Function Assessment
          {assessedCount > 0 && <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#9d174d', color: '#fff', fontWeight: 800 }}>{assessedCount}/6 assessed</span>}
          {imparedCount > 0 && <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#dc2626', color: '#fff', fontWeight: 800 }}>{imparedCount} impaired</span>}
        </button>
      ) : (
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <span style={{ fontWeight: '800', fontSize: '1rem', color: '#9d174d' }}>🖐 Hand Function Assessment</span>
            <button type="button" onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }} onMouseEnter={e => e.target.style.color = '#ef4444'} onMouseLeave={e => e.target.style.color = '#cbd5e1'}>×</button>
          </div>

          {/* Overall grade row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '12px' }}>
            {[{side: 'rt', label: 'Right Hand – Overall'}, {side: 'lt', label: 'Left Hand – Overall'}].map(s => (
              <div key={s.side} style={{ padding: '10px 14px', borderRadius: '10px', background: '#fff', border: '1px solid #fce7f3' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#9d174d', marginBottom: '6px' }}>{s.label}</div>
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                  {HAND_GRADES.map(g => <GradeBtn key={g.id} id={`hand_overall_${s.side}`} gid={g.id} />)}
                </div>
              </div>
            ))}
          </div>

          {/* Items table */}
          <div style={{ overflowX: 'auto', borderRadius: '12px' }}>
          <div style={{ border: '0.5px solid #fce7f3', background: '#fff', minWidth: '340px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr', background: '#fdf2f8', padding: '7px 14px', borderBottom: '0.5px solid #fce7f3' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#9d174d', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Function</div>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#9d174d', textAlign: 'center' }}>Right Hand</div>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#9d174d', textAlign: 'center' }}>Left Hand</div>
            </div>
            {HAND_ITEMS.map((item, idx) => {
              const rtFindings = value[`hand_rt_${item.id}`] || [];
              const ltFindings = value[`hand_lt_${item.id}`] || [];
              const allRtOk = rtFindings.every(f => f === 'Normal' || f === 'Normal grip' || f === 'Not assessed');
              const allLtOk = ltFindings.every(f => f === 'Normal' || f === 'Normal grip' || f === 'Not assessed');
              const isAbnormal = (rtFindings.length > 0 && !allRtOk) || (ltFindings.length > 0 && !allLtOk);
              return (
                <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr', alignItems: 'start', padding: '8px 14px', borderBottom: idx < HAND_ITEMS.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: isAbnormal ? '#fff5f8' : '#fff' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', paddingTop: '3px', paddingRight: '6px' }}>
                    {item.label}
                    {isAbnormal && <div style={{ fontSize: '10px', color: '#dc2626', fontWeight: 700, marginTop: '2px' }}>⚠ Impaired</div>}
                  </div>
                  {['rt', 'lt'].map((side, si) => {
                    const selected = value[`hand_${side}_${item.id}`] || [];
                    return (
                      <div key={side} style={{ paddingLeft: si === 0 ? '6px' : '8px', borderLeft: si === 1 ? '0.5px solid #f1f5f9' : 'none', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                        {item.findings.map(f => {
                          const active = selected.includes(f);
                          const isNorm = f === 'Normal' || f === 'Normal grip';
                          return (
                            <button key={f} type="button" onClick={() => toggle(`hand_${side}_${item.id}`, f)} style={{
                              fontSize: '10px', fontWeight: 600, padding: '3px 8px', borderRadius: '20px', cursor: 'pointer', transition: 'all 0.15s',
                              border: `1px solid ${active ? (isNorm ? '#6ee7b7' : '#fca5a5') : '#e2e8f0'}`,
                              background: active ? (isNorm ? '#ecfdf5' : '#fef2f2') : 'transparent',
                              color: active ? (isNorm ? '#059669' : '#dc2626') : '#64748b'
                            }}>{active ? '✓ ' : ''}{f}</button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          </div>

          {/* Assistive devices */}
          <div style={{ marginTop: '12px', padding: '12px 14px', borderRadius: '12px', background: '#fff', border: '1px solid #fce7f3' }}>
            <div style={{ fontSize: '11px', fontWeight: 800, color: '#9d174d', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assistive Devices / Adaptations</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {ASSISTIVE_HAND_OPTIONS.map(opt => {
                const active = (value.hand_devices || []).includes(opt);
                const isNone = opt === 'None required';
                return (
                  <button key={opt} type="button" onClick={() => toggle('hand_devices', opt)} style={{
                    fontSize: '11px', fontWeight: 600, padding: '4px 11px', borderRadius: '20px', cursor: 'pointer', transition: 'all 0.15s',
                    border: `1px solid ${active ? (isNone ? '#6ee7b7' : '#f9a8d4') : '#e2e8f0'}`,
                    background: active ? (isNone ? '#ecfdf5' : '#fdf2f8') : 'transparent',
                    color: active ? (isNone ? '#059669' : '#9d174d') : '#64748b'
                  }}>{active ? '✓ ' : ''}{opt}</button>
                );
              })}
            </div>
          </div>

          {Object.keys(value).length > 0 && <button type="button" onClick={() => onChange({})} style={{ marginTop: '10px', fontSize: '12px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>Clear all hand function data</button>}
        </div>
      )}
    </div>
  );
};

export default HandFunctionAssessment;
